import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {HTH} from "../HTH";
import {HTHGameAssets} from "../HTHGameAssets";
import {HTHWinConfig, IWinRatioConfig} from "../configs/HTHWinConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {HTHHelper} from "../utils/HTHHelper";
import {HTHSound} from "../sound/HTHSound";
import {HTHGameConfig} from "../HTHGameConfig";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";

export class HTHTotalWinView extends PIXI.Container implements StageLayer {
    public _playingSoundName: string;
    private _countUp: BasicBitmapCountUp;
    private _bgSpine: PIXI.spine.Spine;
    private _waysNumber: PIXI.Text | null;
    private _waysText: PIXI.Text | null;
    private _sparkEffectLeft: TimelineSprite;
    private _sparkEffectRight: TimelineSprite;
    private textStyle: PIXI.TextStyle = new PIXI.TextStyle({
        fontSize: 50,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowAngle: 1.9,
        dropShadowDistance: 1,
        fill: [
            "#f7e67e",
            "#ff8800",
            "#ff0000"
        ],
        fontFamily: "Arial Black",
        fontStyle: "italic",
        stroke: 0x000000,//"#0d1a3f",
        lineJoin: "round",
        strokeThickness: 9
    });

    constructor() {
        super();
        this.name = "TotalWin";
        this.init();
    }

    public show(winRatioConfig: IWinRatioConfig): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            totalWinWays: number = HTH.model.gameData.waysWonTotalSpin;
        this.position.set(0, 0);
        this._waysNumber = new PIXI.Text(totalWinWays.toString(), this.textStyle);
        this._waysText = new PIXI.Text(NolimitApplication.apiPlugin.translations.translate(`Way${totalWinWays > 1 ? "s" : ""}`), this.textStyle);
        const startPos: number = 360 - (this._waysNumber.width + this._waysText.width) * 0.5;
        this._waysNumber.position.set(startPos, 386);
        this._waysText.position.set(startPos + this._waysNumber.width + 20, 386);
        this.addChild(this._bgSpine, this._waysNumber, this._waysText, this._sparkEffectLeft, this._sparkEffectRight, this._countUp);
        this._countUp.stopAt(0);
        tl.add(() => {
            this._countUp.visible = true;
            this._countUp.scale.set(0.5, 0.5);
            this._countUp.alpha = 0;
            this._sparkEffectRight.alpha = 0;
            this._sparkEffectLeft.alpha = 0;
            this.alpha = 0;
            this._bgSpine.alpha = 0;
            this.visible = true;
            this._waysNumber!.visible = totalWinWays > 0;
            this._waysText!.visible = totalWinWays > 0;
            this._waysNumber!.alpha = 0;
            this._waysText!.alpha = 0;
        }, 0);

        const amount: number = HTH.model.mode.includes("FREESPIN") ? HTH.model.data.spinWin : HTH.model.gameData.accumulatedRoundWin;

        const duration: number = HTHGameConfig.lastBigWinPlayedAmount === 0
            ? winRatioConfig.duration : (amount < HTHGameConfig.lastBigWinPlayedAmount + 10 * HTH.model.gameData.playedBetValue) ? 0 : 2;

        tl.to([this, this._bgSpine], {alpha: 1, duration: 0.05});
        if (winRatioConfig.type === HTHWinConfig.TINY || winRatioConfig.type === HTHWinConfig.SMALL) {
            this.scale.set(0.85);
            this.position.set(41, 41);
            tl.add(HTHHelper.playSpineWithTimeline(this._bgSpine, 0, "idle"));
            tl.to([this._countUp, this._waysText, this._waysNumber], {alpha: 1, duration: 0.01}, "<");
            tl.to(this._countUp.scale, {x: 1, y: 1, ease: "sine.in", duration: 0.1}, "<");
        } else {
            this.scale.set(1);
            this.position.set(0, 0);
            tl.add(HTHHelper.playSpineWithTimeline(this._bgSpine, 0, "splash"));
            tl.to([this._countUp, this._waysText, this._waysNumber], {alpha: 1, duration: 0.01}, "<0.15");
            tl.to(this._countUp.scale, {x: 1, y: 1, ease: "sine.in", duration: 0.1}, "<");
        }

        if (winRatioConfig.type !== HTHWinConfig.TINY && winRatioConfig.type !== HTHWinConfig.SMALL && duration > 0) {
            tl.add(() => {
                HTHSound.playTrack(HTHSound.COUNTUP);
            }, "<");
        }
        if (winRatioConfig.type === HTHWinConfig.TINY || winRatioConfig.type === HTHWinConfig.SMALL) {
            tl.add(() => {
                HTHSound.playTrack(winRatioConfig.sound);
            }, "<");
        }
        tl.add(this._countUp.getCountAnimation(0, HTH.model.mode.includes("FREESPIN") ? HTH.model.data.spinWin
            : HTH.model.gameData.accumulatedRoundWin, HTHHelper.isWinBelowStake() ? 0 : duration), "<");
        tl.addLabel("countupCompleted");
        if (winRatioConfig.type !== HTHWinConfig.TINY && winRatioConfig.type !== HTHWinConfig.SMALL && duration > 0) {
            tl.add(this.playSparksAnim(), "countupCompleted-=0.2");
            tl.add(HTH.gameViewContainer.shakeEffect(0.2, 10, true, 10), "<0.1");
            tl.add(() => {
                HTHSound.stopSound(HTHSound.COUNTUP);
                if (HTHGameConfig.bigWinLevelPlayed.includes(true)) {
                    HTHSound.playTrack(HTHSound.ROUND_END_FINAL_WIN);
                } else {
                    HTHSound.playTrack(winRatioConfig.sound);
                }
                this._playingSoundName = winRatioConfig.sound;
            }, "countupCompleted");
        } else if (winRatioConfig.type === HTHWinConfig.LARGE && duration == 0 && HTHGameConfig.bigWinLevelPlayed.includes(true)) {
            tl.add(() => {
                HTHSound.playTrack(HTHSound.ROUND_END_FINAL_WIN);
            }, "countupCompleted");
        }

        return tl;
    }

    public hide(winRatioConfig: IWinRatioConfig): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const hideDuration: number = winRatioConfig.type !== HTHWinConfig.TINY && winRatioConfig.type !== HTHWinConfig.SMALL ? 0.4 : 0.2;
        tl.to(this, {duration: 0.75});
        // tl.to(this._bgSprite, {alpha: 0, duration: 0.2, ease: "sine.out"});
        tl.to(this, {alpha: 0, duration: hideDuration});
        tl.to(this.position, {y: this.position.y - 100, duration: hideDuration, ease: "sine.out"}, "<")
        tl.add(() => {
            this._countUp.stopAt(HTH.model.mode.includes("FREESPIN") ? HTH.model.data.spinWin : HTH.model.gameData.accumulatedRoundWin)
        }, 0)

        tl.add(() => {
            // this.removeChild(this._bgSprite!);
            HTHHelper.resetSpineItem(this._bgSpine);
            this.removeChild(this._waysNumber!);
            this.removeChild(this._waysText!);
            this._waysNumber?.destroy();
            this._waysText?.destroy();
            // this._bgSprite?.destroy();
            this._countUp.visible = false;
            this.visible = false;
        });
        return tl;
    }

    public showInExtraSpin(winRatioConfig: IWinRatioConfig): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            bgSprite: PIXI.Sprite = new PIXI.Sprite(HTH.assetLoader.getTextures("Utility/splash_totalwin")[0]);

        bgSprite.name = "BackSprite";
        bgSprite.anchor.set(0.5, 0.5);
        bgSprite.scale.set(0.8, 0.8);
        bgSprite.position.set(368, 358);
        bgSprite.alpha = 1;
        this.addChild(bgSprite, this._countUp);
        tl.add(() => {
            this._countUp.visible = true;
            this._countUp.stopAt(0);
            this._countUp.scale.set(0.5, 0.5);
            this._countUp.alpha = 1;
            this.alpha = 0;
            this.visible = true;
        });

        tl.add([
            gsap.to(this, {alpha: 1, duration: 0.1}),
            gsap.to(this._countUp.scale, {x: 1, y: 1, ease: "sine.in", duration: 0.1}),

            this._countUp.getCountAnimation(0, HTH.model.data.accumulatedRoundWin, HTHHelper.isWinBelowStake() ? 0 : winRatioConfig.duration)
        ]);
        tl.to(this, {duration: 0.2});
        tl.to(this, {alpha: 0, duration: 0.2});

        tl.add(() => {
            this.removeChild(bgSprite);
            this._countUp.visible = false;
            this.visible = false;
        });
        return tl;
    }

    private init(): void {
        this._countUp = new BasicBitmapCountUp({
            fontName: HTHGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 40,
            tint: 0xffffff
        }, 480, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 320);
        this._countUp.stopAt(0);
        this._countUp.visible = false;

        this._sparkEffectLeft = this.getSparkEffect();
        this._sparkEffectRight = this.getSparkEffect();
        this._sparkEffectLeft.scale.set(-1, 1);
        this.visible = false;

        this._bgSpine = new PIXI.spine.Spine(HTH.assetLoader.getSpineAsset("totalWinBg"));
        this._bgSpine.name = "bgSpine";
        this._bgSpine.position.set(418, 308);
        this._bgSpine.scale.set(0.9, 0.9);

        this.addChild(this._bgSpine, this._sparkEffectLeft, this._sparkEffectRight, this._countUp);
    }

    private playSparksAnim(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._sparkEffectLeft.position.set(this._countUp.x - this._countUp.width / 2 + 20, this._countUp.y);
            this._sparkEffectRight.position.set(this._countUp.x + this._countUp.width / 2 - 20, this._countUp.y);
        }, 0)
        tl.to([this._sparkEffectLeft, this._sparkEffectRight], {alpha: 1, duration: 0.1});
        tl.add(<gsap.core.Timeline><unknown>this._sparkEffectRight.getAnimationAutoShowHide());
        tl.add(<gsap.core.Timeline><unknown>this._sparkEffectLeft.getAnimationAutoShowHide(), "<");
        return tl;
    }

    private getSparkEffect(): TimelineSprite {
        let ts: TimelineSprite = new TimelineSprite(HTH.assetLoader.getTextures("countUpSparkEffect"));
        ts.position.set(360, 387);
        ts.anchor.set(0, 0.5);
        ts.blendMode = PIXI.BLEND_MODES.ADD;
        ts.scale.set(1, 1);
        ts.visible = false;
        ts.name = "countUpSparkEffect";
        this.addChild(ts);
        return ts;
    }

}